import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import Image from "common/blocks/Image";
import SmartLink from "common/wrapper/SmartLink";
import useMediaQuery from "hooks/useMediaQuery/useMediaQuery";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useState } from "react";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import styles from "./RotatingBanners.module.scss";
import { ResolvedRotatingBanner } from "resolvers/base/rotatingBanner";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";

const cx = classNames.bind(styles);

export interface ResolvedHomePageRotatingBanner {
    type: ContentTypeCodenames.RotatingBannerRow;
}

export type RotatingBannersProps = {
    rotatingBanners: ResolvedRotatingBanner[];
    overideShowRotatingBanners?: boolean;
};

const RotatingBanners = ({
    rotatingBanners,
    overideShowRotatingBanners = false,
}: RotatingBannersProps) => {
    const router = useRouter();
    const [isClientSide, setIsClientSide] = useState(false);
    const [isCtaVisible, setIsCtaVisible] = useState(false);
    const isDesktop = useMediaQuery("(min-width: 1440px)");

    const rotatingBanner = useMemo(
        () =>
            rotatingBanners?.find((banner) => {
                if (router.route === banner.urlMatch || overideShowRotatingBanners) return true;
                if (banner.urlMatch === "/") return true;

                return false;
            }),
        [rotatingBanners, router.route, overideShowRotatingBanners]
    );

    const [bannerCount, setBannerCount] = useState(rotatingBanner?.body.length ?? 0);

    useEffect(() => {
        setIsClientSide(true);
    }, []);

    useEffect(() => {
        if (isClientSide && rotatingBanner?.body) {
            if (!isDesktop) {
                setIsCtaVisible(true);
            } else if (bannerCount === 1) {
                setIsCtaVisible(true);
            } else {
                setIsCtaVisible(false);
            }
        }
    }, [isDesktop, rotatingBanner, bannerCount, isClientSide]);

    const swiperProps = useMemo(
        () =>
            rotatingBanners
                ? {
                      loop: !isDesktop,
                      slidesPerView: isDesktop ? 3 : 1,
                      speed: 300,
                      autoplay: ((!!rotatingBanner?.body && bannerCount > 1) || !isDesktop) && {
                          delay: 3000,
                          disableOnInteraction: false,
                      },
                      allowTouchMove: false,
                      modules: [Autoplay],
                  }
                : {},
        [rotatingBanner, isDesktop, rotatingBanners, bannerCount]
    );

    if (!rotatingBanner?.body) return null;

    const LinkWrapper = ({
        children,
        link,
        target,
        textColour,
    }: {
        children: React.ReactNode;
        link: string | null;
        target: string | null;
        textColour: string;
    }) => {
        return !link ? (
            <>{children}</>
        ) : (
            <SmartLink href={link}>
                <a
                    style={{ color: textColour }}
                    className={cx("link")}
                    target={target ?? undefined}
                >
                    {children}
                </a>
            </SmartLink>
        );
    };

    return (
        <div
            className={cx("valueAdd")}
            style={{ backgroundColor: rotatingBanner.backgroundColour }}
        >
            <div className={cx("widthConstrain")}>
                <Swiper {...swiperProps}>
                    {rotatingBanner?.body?.map(
                        ({ icon, text, shortDescription, link, target, textColour, cta }) => (
                            <SwiperSlide className={cx(`slide-${bannerCount}`)} key={text}>
                                <div className={cx("contentWrapper", !!cta && cx("withCta"))}>
                                    <LinkWrapper
                                        link={link}
                                        target={target}
                                        textColour={textColour}
                                    >
                                        <div className={cx("ad")}>
                                            {!!icon && (
                                                <div
                                                    className={cx(
                                                        "iconWrapper",
                                                        !!cta && cx("hasCta")
                                                    )}
                                                >
                                                    <Image
                                                        className={cx("icon")}
                                                        alt={icon.alt}
                                                        src={icon.src}
                                                        layout="fill"
                                                        objectFit="cover"
                                                    />
                                                </div>
                                            )}
                                            <div className={cx("content")}>
                                                {!!text && (
                                                    <span className={cx("title")}>{text}</span>
                                                )}
                                                {!!shortDescription && (
                                                    <span
                                                        className={cx(
                                                            "description",
                                                            !!cta && cx("hasCta")
                                                        )}
                                                    >
                                                        {shortDescription}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </LinkWrapper>
                                    {!!cta && isCtaVisible && (
                                        <Button
                                            className={cx("button", isCtaVisible, cta.priority)}
                                            href={cta.link}
                                            prefetch={false}
                                            theme={
                                                cta.priority == "secondary"
                                                    ? "accent"
                                                    : cta.priority
                                            }
                                            type={"button"}
                                            target={cta.target}
                                        >
                                            {cta.text}
                                        </Button>
                                    )}
                                </div>
                            </SwiperSlide>
                        )
                    )}
                </Swiper>
            </div>
        </div>
    );
};

export default RotatingBanners;
